import React from "react"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage from "@irati/utils/lang"
import {H4} from "@irati/ui/src/Typography"
import searchNothingFound from "@irati/ui/images/search_nothing_found.svg"

import {useTheme} from "@irati/ui/src/global/useTheme"
import {formatFromTimeToHoursAndMinutes} from "@irati/wellness/utils"
import {RouterLink} from "@irati/ui/src/Link"
import Button from "@irati/ui/src/Button/Button"
import {WellnessMedia} from "@irati/types"
import Markdown from "@irati/ui/src/Markdown"
import styled from "styled-components"
import DetailsPill from "../MediaCard/DetailsPill"
import MediaImportantTags from "../MediaImportantTags"

export const WellnessSearchResultList = React.memo(ResultList)

interface Props {
  results: WellnessMedia[] | undefined
  count: number
  onClose: () => void
  searchTerm: string
  maxResults: number
}

function ResultList({results, count, onClose, searchTerm, maxResults}: Props) {
  const theme = useTheme()
  return (
    <Flex direction="column">
      {!results ? null : results.length > 0 ? null : <EmptyState onClick={onClose} />}

      <Flex direction="column" gap={3}>
        {results?.map((media) => (
          <StyledRouterLink plain key={media.id} to={`/details/${media.id}`}>
            <Flex alignItems="stretch" gap={3}>
              <div style={{width: "218px", height: "122px", flexShrink: 0, borderRadius: theme.border.radius.large}}>
                <img
                  src={media.thumbnail}
                  alt={media.title}
                  style={{
                    background: theme.colors.greys.light,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: theme.border.radius.large
                  }}
                />
              </div>

              <Flex direction="column" justifyContent="space-around">
                <H4>{media.title}</H4>
                <MediaImportantTags media={media} color={theme.colors.utility.contrast} />
                <div style={{width: "fit-content"}}>
                  <DetailsPill text={formatFromTimeToHoursAndMinutes(media.duration)} type={media.type} />
                </div>
              </Flex>
            </Flex>
          </StyledRouterLink>
        ))}
      </Flex>

      {count > maxResults ? (
        <Flex margin={[5, 0]} alignItems="center" justifyContent="space-between">
          <RouterLink to={`/search?q=${searchTerm}`}>
            <FormattedMessage id="wellness.search.showAllResults" />
          </RouterLink>
          <SmallText>
            <FormattedMessage id="wellness.search.resultsCount" values={{shownResults: results?.length, allResults: count}} />
          </SmallText>
        </Flex>
      ) : null}
    </Flex>
  )
}

function EmptyState({onClick}: {onClick: () => void}) {
  return (
    <Flex gap={3}>
      <img src={searchNothingFound} alt="No results found" />

      <Flex direction="column">
        <Markdown
          source="wellness.search.nothingFound"
          values={{
            link: (
              <Button cta onClick={onClick} large>
                <FormattedMessage id="wellness.search.nothingFoundLinkName" />
              </Button>
            )
          }}
        />
      </Flex>
    </Flex>
  )
}

const StyledRouterLink = styled(RouterLink)`
  &:hover,
  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.primary1_600};
    outline-offset: -1px;
    border-radius: ${({theme}) => theme.border.radius.large};
    background-color: ${({theme}) => theme.colors.greys.lightest};
  }
` as typeof RouterLink

const SmallText = styled.p`
  color: ${({theme}) => theme.colors.greys.mediumDark};
  font-size: 12px;
  line-height: 16px;
`
